import React, { useContext, useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
} from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../contexts/GlobalContext"

// Importación para generar íconos dinámicos según estándar
import getIconComponent from "../utils/getIconComponent"

import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import SearchIcon from "@mui/icons-material/Search"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

// Helper monetario
const formatMoneda = (valor) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(valor || 0)
}

const ConsDatosRecaudarIndex = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Controladores de estado visual: Inicia cerrado y en 0 (reposo)
  const [expanded, setExpanded] = useState(true)
  const [refreshTableKey, setRefreshTableKey] = useState(0)

  // Estado centralizado de filtros principales
  const [filtros, setFiltros] = useState({
    facnumfac: "",
    clicodigo: "",
    clinombre: "",
    soloConSaldo: "S", // 'S' para mostrar solo cuentas con mora/saldo
  })

  const handleInputChange = (field, value) => {
    setFiltros((prev) => ({ ...prev, [field]: value }))
  }

  const handleFiltrar = () => {
    // Al presionar buscar, la llave cambia, forzando a la tabla a enviar la petición Axios
    setRefreshTableKey((prev) => prev + 1)
    setExpanded(false)
  }

  // Renderizador de Colores para Estado de la Factura
  const renderEstado = (status) => {
    const statusMap = {
      A: { text: "ACTIVA", color: "#1976d2" },
      P: { text: "PENDIENTE", color: "#d32f2f" },
      C: { text: "CANCELADA", color: "#2e7d32" },
    }
    const current = statusMap[status] || { text: status, color: "black" }

    return <span style={{ color: current.color, fontWeight: "bold" }}>{current.text}</span>
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
            color: "#196C87",
          }}
        >
          <MonetizationOnIcon sx={{ fontSize: 36, mr: 1 }} />
          <b>Consulta de Datos para Recaudación en Terreno</b>
        </div>

        <Box className={StyledRoot}>
          {/* ACORDEÓN DE FILTROS */}
          <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{ mb: 3, borderRadius: 2, boxShadow: 3, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "#f5f7fa", borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Filtros de Localización de Cartera
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    fullWidth
                    label="Número de Factura"
                    size="small"
                    value={filtros.facnumfac}
                    onChange={(e) => handleInputChange("facnumfac", e.target.value.toUpperCase())}
                    placeholder="Ej. F24..."
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    label="Código / RUC Cliente"
                    size="small"
                    value={filtros.clicodigo}
                    onChange={(e) => handleInputChange("clicodigo", e.target.value.toUpperCase())}
                  />
                </Grid>

                <Grid item xs={12} sm={8} md={5}>
                  <TextField
                    fullWidth
                    label="Nombre del Cliente"
                    size="small"
                    value={filtros.clinombre}
                    onChange={(e) => handleInputChange("clinombre", e.target.value.toUpperCase())}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={filtros.soloConSaldo === "S"}
                        onChange={(e) => handleInputChange("soloConSaldo", e.target.checked ? "S" : "N")}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight="bold">
                        Solo con Saldo
                      </Typography>
                    }
                  />
                </Grid>

                <Grid item xs={12} display="flex" justifyContent="flex-end" mt={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SearchIcon />}
                    onClick={handleFiltrar}
                    sx={{ px: 4, fontWeight: "bold" }}
                  >
                    Ejecutar Búsqueda
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* TABLA DE RESULTADOS TRANSSACCIONALES (En reposo hasta buscar) */}
          <CustomConditionalActionsTableServer
            key={refreshTableKey}
            endpoint={refreshTableKey === 0 ? "" : "/ConsDatosparaRecaudar/getAllFacturas"}
            endpointJson={{
              externalFilters: filtros,
            }}
            errorMsgFilterSearch="Error al cargar las facturas para recaudación."
            queryKeyModal="DatosRecaudacionData"
            perPage={15}
            rowActionsWidthTable={100}
            rowActions={(row) => {
              const acciones = []

              const detalleAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "BUSCAR",
              )

              if (detalleAction) {
                acciones.push({
                  label: "Ver Hoja de Ruta",
                  key: "BUSCAR",
                  icon: getIconComponent(detalleAction?.accnameicono, detalleAction?.acctipoico),
                  onClick: (row) =>
                    navigate("buscar", {
                      state: { facnumfac: row.original.facnumfac },
                    }),
                })
              }

              return acciones
            }}
            topToolbarCustomActions={() => []} // Sin botón de crear ni importar
            columnsTable={[
              {
                accessorKey: "facnumfac",
                header: "Factura No.",
                size: 160,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold", color: "#196C87" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "cliruc",
                header: "RUC",
                size: 130,
              },
              {
                accessorKey: "clinombre",
                header: "Nombre del Cliente",
                size: 280,
              },
              {
                accessorKey: "facfecemi",
                header: "Fec. Emisión",
                size: 110,
                Cell: ({ cell }) => <span>{normalFormatDate(cell.getValue())}</span>,
              },
              {
                accessorKey: "facstatus",
                header: "Estado",
                size: 110,
                Cell: ({ cell }) => renderEstado(cell.getValue()),
              },
              {
                accessorKey: "factotal",
                header: "Total Factura",
                size: 120,
                Cell: ({ cell }) => <span>{formatMoneda(cell.getValue())}</span>,
              },
              {
                accessorKey: "facsaldo",
                header: "Saldo a Cobrar",
                size: 130,
                Cell: ({ cell }) => (
                  <span style={{ fontWeight: "bold", color: "#d32f2f" }}>{formatMoneda(cell.getValue())}</span>
                ),
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default ConsDatosRecaudarIndex
